import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { getProject, getNextProject } from '../data/projects';
import { useReveal } from '../hooks/useReveal';
import { useMeta } from '../hooks/useMeta';
import ProjectViewer from '../components/ProjectViewer';
import '../styles/project.css';

const ProjectPage = () => {
  const { slug } = useParams();
  const project = getProject(slug);
  useReveal([slug]);
  useMeta(
    project && {
      title: `${project.title} — ${project.subtitle}`,
      description: project.summary,
      image: project.image,
      path: `/projets/${slug}`
    }
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) return <Navigate to="/" replace />;

  const next = getNextProject(slug);

  return (
    <main className="project">
      <div className="shell">
        <Link to="/#work" className="project-back">
          <ArrowLeft size={15} aria-hidden="true" />
          Tous les projets
        </Link>

        <header className="project-head">
          <p className="eyebrow">{project.subtitle}</p>
          <h1 className="project-title">{project.title}</h1>
          <p className="project-summary">{project.summary}</p>
        </header>

        <div data-reveal>
          <ProjectViewer project={project} />
        </div>

        {/* Fiche technique */}
        <div className="fiche">
          <section className="fiche-tile fiche-tile--wide" data-reveal>
            <h2 className="fiche-label">Le projet</h2>
            <p className="fiche-text">{project.description}</p>
          </section>

          <section className="fiche-tile" data-reveal>
            <h2 className="fiche-label">Repères</h2>
            <dl className="fiche-specs">
              <div>
                <dt>Année</dt>
                <dd>{project.year}</dd>
              </div>
              <div>
                <dt>Client</dt>
                <dd>{project.client}</dd>
              </div>
              <div>
                <dt>Rôle</dt>
                <dd>{project.role}</dd>
              </div>
            </dl>
          </section>

          {project.highlights?.length > 0 && (
            <section className="fiche-tile fiche-tile--wide" data-reveal>
              <h2 className="fiche-label">Points clés</h2>
              <ul className="fiche-list">
                {project.highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="fiche-tile" data-reveal>
            <h2 className="fiche-label">Outils</h2>
            <ul className="fiche-tags">
              {project.tags.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          </section>

          {project.process?.length > 0 && (
            <section className="fiche-tile fiche-tile--full" data-reveal>
              <h2 className="fiche-label">Démarche</h2>
              <ol className="fiche-process">
                {project.process.map((step) => (
                  <li key={step.title}>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {project.outcome && (
            <section className="fiche-tile fiche-tile--full" data-reveal>
              <h2 className="fiche-label">Résultat</h2>
              <p className="fiche-text">{project.outcome}</p>
            </section>
          )}

          {project.links?.length > 0 && (
            <section className="fiche-tile fiche-tile--full fiche-links" data-reveal>
              {project.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`fiche-link ${link.primary ? 'is-primary' : ''}`}
                >
                  {link.label}
                  <ArrowUpRight size={18} aria-hidden="true" />
                </a>
              ))}
            </section>
          )}
        </div>
      </div>

      <Link to={`/projets/${next.slug}`} className="project-next">
        <div className="shell project-next-inner">
          <span className="project-next-label">Projet suivant</span>
          <span className="project-next-title">{next.title}</span>
          <ArrowRight size={28} aria-hidden="true" className="project-next-arrow" />
        </div>
      </Link>
    </main>
  );
};

export default ProjectPage;
